import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Plus, Trash2, FileText, ClipboardList, Download, Gauge, Wrench, CalendarClock,
  Loader2, Package, ExternalLink, MoreHorizontal, Droplets, CalendarCheck2, CalendarRange, History, Eye, MapPin, Building2, MapPinned,
  ShieldCheck, FolderOpen, ListTodo,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/StatusPill";
import { ReportTypeBadge } from "@/components/Badges";
import { MachineDialog } from "@/components/MachineDialog";
import { DatePicker } from "@/components/DatePicker";
import { EmptyState } from "@/components/EmptyState";
import { DeadlineActionDialog } from "@/components/calendar/DeadlineActionDialog";
import { formatDate, num, daysText, todayISO, CATEGORY_LABEL, CATEGORY_OPTIONS } from "@/lib/format";
import { LOCATION_SOURCE_LABEL } from "@/lib/geo";
import { downloadReportPdf, openReportPdf } from "@/lib/pdf";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { MachineGallery } from "@/components/MachineGallery";
import { MachineDocuments } from "@/components/machine/MachineDocuments";
import { RegisterControlDialog } from "@/components/machine/RegisterControlDialog";
import { MachineTasks } from "@/components/machine/MachineTasks";
import { fileUrl } from "@/lib/files";
import { rememberMachine } from "@/lib/recent";
import { useAuth, useFeatures } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const InfoRow = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-2.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium" data-testid={testid}>{value ?? "—"}</span>
  </div>
);

const PART_EMPTY = { name: "", category: "filter", part_number: "", quantity: "1", unit: "stk", notes: "" };

const StatusCard = ({ icon: Icon, label, value, sub, status, testid, dark, actions }) => (
  <Card className={cn("shadow-sm", dark && "border-transparent bg-[#0B0D10] text-white")} data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-lg", dark ? "bg-white/10" : "bg-muted")}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-white/60" : "text-muted-foreground")}>{label}</div>
          <div className="truncate font-heading text-lg font-bold tabular-nums">{value}</div>
          {sub && <div className={cn("text-xs", dark ? "text-white/60" : "text-muted-foreground")}>{sub}</div>}
        </div>
        {status && <StatusPill status={status} />}
      </div>
      {actions && <div className="mt-3 flex flex-wrap gap-1.5">{actions}</div>}
    </CardContent>
  </Card>
);

export default function MaskinDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { map_enabled } = useFeatures();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [controlOpen, setControlOpen] = useState(false);
  const [tab, setTab] = useState("oversikt");
  const [hoursOpen, setHoursOpen] = useState(false);
  const [hoursVal, setHoursVal] = useState("");
  const [hoursDate, setHoursDate] = useState(todayISO());
  const [partOpen, setPartOpen] = useState(false);
  const [partForm, setPartForm] = useState(PART_EMPTY);
  const [editingPart, setEditingPart] = useState(null);
  const [delMachineOpen, setDelMachineOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deadlineDialog, setDeadlineDialog] = useState({ open: false, mode: "plan", kind: "service" });
  const [previewReport, setPreviewReport] = useState(null);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    api
      .get(`/machines/${id}`)
      .then((r) => {
        setM(r.data);
        rememberMachine(r.data);
      })
      .catch(() => navigate("/maskiner"))
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(() => {
    load();
  }, [load]);

  const saveHours = async () => {
    setBusy(true);
    try {
      await api.patch(`/machines/${id}/hours`, { current_hours: Number(hoursVal), date: hoursDate });
      toast.success("Avlesing registrert.");
      setHoursOpen(false);
      setHoursVal("");
      load();
    } catch (e) {
      toast.error("Kunne ikkje oppdatere timetal.");
    } finally {
      setBusy(false);
    }
  };

  const openNewPart = () => { setEditingPart(null); setPartForm(PART_EMPTY); setPartOpen(true); };
  const openEditPart = (p) => { setEditingPart(p); setPartForm({ ...PART_EMPTY, ...p, quantity: String(p.quantity ?? "1"), part_number: p.part_number || "", unit: p.unit || "", notes: p.notes || "" }); setPartOpen(true); };
  const savePart = async (e) => {
    e.preventDefault();
    if (!partForm.name.trim()) return toast.error("Delnamn er påkrevd.");
    setBusy(true);
    try {
      const payload = { name: partForm.name, category: partForm.category, part_number: partForm.part_number, quantity: Number(partForm.quantity) || 1, unit: partForm.unit, notes: partForm.notes };
      if (editingPart) await api.put(`/parts/${editingPart.id}`, payload);
      else await api.post("/parts", { machine_id: id, ...payload });
      toast.success(editingPart ? "Del oppdatert." : "Del lagt til.");
      setPartOpen(false);
      load();
    } catch (err) {
      toast.error("Noko gjekk gale.");
    } finally {
      setBusy(false);
    }
  };
  const deletePart = async (p) => {
    try {
      await api.delete(`/parts/${p.id}`);
      toast.success("Del fjerna.");
      load();
    } catch (e) {
      toast.error("Kunne ikkje slette del.");
    }
  };

  const deleteMachine = async () => {
    try {
      await api.delete(`/machines/${id}`);
      toast.success("Maskin sletta.");
      navigate("/maskiner");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje slette.");
    }
  };

  const dl = async (r) => { try { await downloadReportPdf(r); } catch (e) { toast.error("Kunne ikkje laste ned PDF."); } };
  const openPdf = async (r) => { try { await openReportPdf(r); } catch (e) { toast.error("Kunne ikkje opne PDF."); } };

  if (loading || !m) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-3 sm:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const st = m.status || {};
  const parts = m.parts || [];
  const reports = m.reports || [];
  const deadlineLogs = m.deadline_logs || [];
  const deadlineEvent = (kind) => ({
    id: `${m.id}:${kind}`,
    machine_id: m.id,
    kind,
    label: m.label,
    customer_name: m.customer_name,
    date: kind === "service" ? st.next_service_date : st.next_control_date,
    status: kind === "service" ? st.service_status : st.control_status,
    days_remaining: kind === "service" ? st.service_days_remaining : st.control_days_remaining,
    planned_date: kind === "service" ? st.planned_service_date : st.planned_control_date,
    planned_note: kind === "service" ? st.planned_service_note : st.planned_control_note,
  });
  const LOG_ACTION = { move: "Frist flytta", plan: "Planlagt", unplan: "Plan fjerna", report: "Oppdatert av rapport", control: "Årskontroll utført" };
  const CONTROL_RESULT = { godkjent: "Godkjent", godkjent_med_merknad: "Godkjent m/ merknad", ikkje_godkjent: "Ikkje godkjent" };
  const controlHistory = deadlineLogs.filter((l) => l.action === "control");
  const documents = m.documents || [];
  const specs = [
    ["Motor", m.engine], ["Driftsvekt", m.weight_kg ? `${num(m.weight_kg)} kg` : null],
    ["Motorolje", m.oil_type], ["Oljemengd", m.oil_capacity_l ? `${m.oil_capacity_l} l` : null],
    ["Hydraulikkolje", m.hydraulic_oil_type], ["Gir-/transmisjonsolje", m.gear_oil_type], ["Kjølevæske", m.coolant_type],
  ].filter(([, v]) => v);
  const primaryImage = (m.images || []).find((i) => i.id === m.primary_image_file_id) || (m.images || [])[0];

  return (
    <div className="space-y-5" data-testid="machine-detail">
      <button type="button" onClick={() => navigate("/maskiner")} className="hidden h-9 items-center gap-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground lg:inline-flex" data-testid="back-to-machines">
        <ArrowLeft className="h-4 w-4" /> Maskiner
      </button>

      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          {primaryImage && (
            <img src={fileUrl(primaryImage.id, { size: "thumb" })} alt={m.label} className="hidden h-20 w-28 shrink-0 rounded-lg border object-cover shadow-sm sm:block" data-testid="machine-hero-image" />
          )}
          <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="machine-title">{m.label}</h1>
            <StatusPill status={st.overall_status} size="md" testid="machine-status-badge" />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            <button type="button" className="font-medium text-foreground hover:underline" onClick={() => navigate(`/maskiner?customer=${m.customer_id}`)} data-testid="machine-customer-link">{m.customer_name || "Ukjend kunde"}</button>
            {m.serial_number ? ` · SN ${m.serial_number}` : ""}{m.year ? ` · ${m.year}` : ""}{m.category ? ` · ${m.category}` : ""}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground" data-testid="machine-location-line">
            {m.location?.lat != null ? (
              <><MapPin className="h-3.5 w-3.5 text-primary" /> <span className="font-medium text-foreground">{m.location.address || `${Number(m.location.lat).toFixed(4)}, ${Number(m.location.lng).toFixed(4)}`}</span> · {LOCATION_SOURCE_LABEL[m.location.source] || "Overstyrt plassering"}{m.location.note ? ` · ${m.location.note}` : ""}</>
            ) : m.customer_address ? (
              <><Building2 className="h-3.5 w-3.5" /> {m.customer_address} · kundens adresse{!m.customer_has_coords && " (utan koordinatar)"}</>
            ) : (
              <><Building2 className="h-3.5 w-3.5" /> {map_enabled ? "Inga adresse – legg inn adresse på kunden eller plassering på maskina." : "Inga adresse – legg inn adresse på kunden."}</>
            )}
            {map_enabled && (m.location?.lat != null || m.customer_has_coords) && (
              <button type="button" onClick={() => navigate(`/kart?d=0&customer=${m.customer_id}`)} className="inline-flex items-center gap-1 font-semibold text-foreground underline-offset-2 hover:underline" data-testid="machine-show-on-map">
                <MapPinned className="h-3.5 w-3.5" /> Vis på kart
              </button>
            )}
          </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate(`/rapportar/ny-service?machine=${id}`)} data-testid="machine-new-report-button"><FileText /> Ny servicerapport</Button>
          <Button variant="outline" onClick={() => navigate(`/rapportar/ny-jobb?machine=${id}`)} data-testid="machine-new-jobb-button"><ClipboardList /> Ny jobbrapport</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Fleire handlingar" data-testid="machine-more-menu"><MoreHorizontal className="h-5 w-5" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setControlOpen(true)} data-testid="register-control-button" className="cursor-pointer"><ShieldCheck className="mr-2 h-4 w-4" /> Registrer årskontroll</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setHoursOpen(true)} data-testid="update-hours-button" className="cursor-pointer"><Gauge className="mr-2 h-4 w-4" /> Ny timeavlesing</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid="edit-machine-button" className="cursor-pointer"><Pencil className="mr-2 h-4 w-4" /> Rediger maskin</DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setDelMachineOpen(true)} data-testid="delete-machine-button" className="cursor-pointer text-destructive focus:text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Slett maskin</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid gap-3 sm:grid-cols-3">
        <StatusCard
          icon={Wrench}
          label="Neste service"
          value={st.next_service_date ? formatDate(st.next_service_date) : "Ikkje sett"}
          sub={st.planned_service_date ? `Planlagt ${formatDate(st.planned_service_date)}` : st.service_status ? daysText(st.service_days_remaining) : "Sett intervall eller dato"}
          status={st.service_status}
          testid="machine-service-card"
          actions={st.next_service_date && (
            <>
              <button type="button" onClick={() => setDeadlineDialog({ open: true, mode: "plan", kind: "service" })} className="inline-flex h-8 items-center gap-1 rounded-md border bg-card px-2 text-xs font-semibold hover:bg-accent" data-testid="machine-plan-service"><CalendarCheck2 className="h-3.5 w-3.5" /> Planlegg</button>
              <button type="button" onClick={() => setDeadlineDialog({ open: true, mode: "move", kind: "service" })} className="inline-flex h-8 items-center gap-1 rounded-md border bg-card px-2 text-xs font-semibold hover:bg-accent" data-testid="machine-move-service"><CalendarRange className="h-3.5 w-3.5" /> Flytt frist</button>
            </>
          )}
        />
        <StatusCard
          icon={CalendarClock}
          label="Årskontroll"
          value={st.next_control_date ? formatDate(st.next_control_date) : "Ikkje sett"}
          sub={st.planned_control_date ? `Planlagt ${formatDate(st.planned_control_date)}` : st.control_status ? daysText(st.control_days_remaining) : "Registrer siste kontroll"}
          status={st.control_status}
          testid="machine-control-card"
          actions={(
            <>
              <button type="button" onClick={() => setControlOpen(true)} className="inline-flex h-8 items-center gap-1 rounded-md border border-transparent bg-[#0B0D10] px-2 text-xs font-semibold text-white hover:bg-[#0B0D10]/90 dark:bg-white dark:text-[#0B0D10]" data-testid="machine-register-control"><ShieldCheck className="h-3.5 w-3.5" /> Registrer utført</button>
              {st.next_control_date && (
                <>
                  <button type="button" onClick={() => setDeadlineDialog({ open: true, mode: "plan", kind: "annual" })} className="inline-flex h-8 items-center gap-1 rounded-md border bg-card px-2 text-xs font-semibold hover:bg-accent" data-testid="machine-plan-annual"><CalendarCheck2 className="h-3.5 w-3.5" /> Planlegg</button>
                  <button type="button" onClick={() => setDeadlineDialog({ open: true, mode: "move", kind: "annual" })} className="inline-flex h-8 items-center gap-1 rounded-md border bg-card px-2 text-xs font-semibold hover:bg-accent" data-testid="machine-move-annual"><CalendarRange className="h-3.5 w-3.5" /> Flytt frist</button>
                </>
              )}
            </>
          )}
        />
        <StatusCard dark icon={Gauge} label="Timetal" value={m.current_hours != null ? `${num(m.current_hours)} t` : "—"} sub={m.last_hours_read_date ? `Avlese ${formatDate(m.last_hours_read_date)}` : "Ingen avlesing"} testid="machine-hours-card" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsList className="inline-flex h-11 w-max min-w-full sm:min-w-0 sm:w-auto">
            <TabsTrigger value="oversikt" className="h-9 px-3 text-xs sm:text-sm" data-testid="tab-oversikt">Oversikt</TabsTrigger>
            <TabsTrigger value="deler" className="h-9 px-3 text-xs sm:text-sm" data-testid="tab-deler">Servicekort <span className="ml-1 rounded-full bg-muted px-1.5 text-[11px] tabular-nums sm:ml-1.5 sm:text-xs">{parts.length}</span></TabsTrigger>
            <TabsTrigger value="dokument" className="h-9 px-3 text-xs sm:text-sm" data-testid="tab-dokument">Dokument <span className="ml-1 rounded-full bg-muted px-1.5 text-[11px] tabular-nums sm:ml-1.5 sm:text-xs">{documents.length}</span></TabsTrigger>
            <TabsTrigger value="rapportar" className="h-9 px-3 text-xs sm:text-sm" data-testid="tab-rapportar">Rapportar <span className="ml-1 rounded-full bg-muted px-1.5 text-[11px] tabular-nums sm:ml-1.5 sm:text-xs">{reports.length}</span></TabsTrigger>
            <TabsTrigger value="historikk" className="h-9 px-3 text-xs sm:text-sm" data-testid="tab-historikk">Timetal</TabsTrigger>
          </TabsList>
        </div>

        {/* ---- Oversikt ---- */}
        <TabsContent value="oversikt" className="mt-4 space-y-4">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base font-bold">Maskindetaljar</CardTitle></CardHeader>
            <CardContent className="grid gap-x-10 sm:grid-cols-2">
              <div className="divide-y divide-border">
                <InfoRow label="Maskinmerke" value={m.brand} />
                <InfoRow label="Type / modell" value={m.type} />
                <InfoRow label="Serie-/chassisnr" value={m.serial_number} />
                <InfoRow label="Kundens maskinnr" value={m.internal_number} testid="machine-internal-number" />
                <InfoRow label="Kategori" value={m.category} />
              </div>
              <div className="divide-y divide-border">
                <InfoRow label="Årsmodell" value={m.year} />
                <InfoRow label="Serviceintervall" value={m.service_interval_months ? `${m.service_interval_months} md${m.service_interval_hours ? ` / ${num(m.service_interval_hours)} t` : ""}` : (m.service_interval_hours ? `${num(m.service_interval_hours)} t` : "—")} />
                <InfoRow label="Sist service" value={m.last_service_date ? formatDate(m.last_service_date) : "—"} />
                <InfoRow label="Sist årskontroll" value={m.control_last_date ? formatDate(m.control_last_date) : "—"} testid="machine-control-last-date" />
                <InfoRow label="Kontrollintervall" value={m.control_interval_months ? `${m.control_interval_months} md` : "—"} />
              </div>
              {m.notes && <div className="col-span-full mt-3 rounded-lg bg-muted/60 p-3 text-sm">{m.notes}</div>}
            </CardContent>
          </Card>

          <MachineGallery machine={m} onChange={(updated) => setM((prev) => ({ ...prev, ...updated, parts: prev.parts, reports: prev.reports, deadline_logs: prev.deadline_logs, documents: prev.documents }))} />

          <MachineTasks machine={m} />

          <Card className="shadow-sm" data-testid="machine-deadline-log">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><History className="h-4 w-4 text-primary" /> Fristlogg</CardTitle>
            </CardHeader>
            <CardContent>
              {deadlineLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen endringar enno. Flytta fristar, planlagde datoar, utførte årskontrollar og rapportar som oppdaterer fristen blir logga her.</p>
              ) : (
                <div className="divide-y divide-border rounded-lg border">
                  {deadlineLogs.slice(0, 10).map((l) => (
                    <div key={l.id} className="flex flex-col gap-1 px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <span className="font-semibold">{LOG_ACTION[l.action] || l.action}</span>
                        <span className="text-muted-foreground"> · {l.kind === "annual" ? "Årskontroll" : "Service"}</span>
                        {(l.old_date || l.new_date) && (
                          <span className="ml-1 tabular-nums text-muted-foreground">
                            {l.old_date ? formatDate(l.old_date) : "—"} → {l.new_date ? formatDate(l.new_date) : "—"}
                          </span>
                        )}
                        {l.reason && <div className="truncate text-xs text-muted-foreground">«{l.reason}»</div>}
                      </div>
                      <div className="shrink-0 text-xs text-muted-foreground">{l.by_name}{l.created_at ? ` · ${formatDate(l.created_at)}` : ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Servicekort: væsker + filter/delar ---- */}
        <TabsContent value="deler" className="mt-4 space-y-4">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><Droplets className="h-4 w-4 text-primary" /> Oljer og væsker</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">Kva maskina skal ha – éin stad, kjem med i servicerapporten.</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEditOpen(true)} data-testid="edit-specs-button"><Pencil /> Rediger</Button>
            </CardHeader>
            <CardContent>
              {specs.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen spesifikasjonar registrert. Legg inn motorolje, hydraulikkolje og kjølevæske – eller bruk «Hent info med AI» i redigering.</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="machine-specs">
                  {specs.map(([k, v]) => (
                    <div key={k} className="rounded-lg border bg-muted/40 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</div>
                      <div className="mt-0.5 text-sm font-medium">{v}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div>
                <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><Package className="h-4 w-4 text-primary" /> Filter og delar til service</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">Delenummer og mengd per service.</p>
              </div>
              <Button size="sm" onClick={openNewPart} data-testid="parts-add-button"><Plus /> Legg til del</Button>
            </CardHeader>
            <CardContent className="pt-0">
              {parts.length === 0 ? (
                <EmptyState icon={Package} title="Ingen filter eller oljer registrert" description="Legg inn filter, oljer og kjølevæske med delenummer – så veit alle kva maskina skal ha, og det kjem med i servicerapporten." action={<Button onClick={openNewPart}><Plus /> Legg til del</Button>} />
              ) : (
                <>
                  <div className="space-y-2 sm:hidden">
                    {parts.map((p) => (
                      <div key={p.id} className="flex items-center gap-3 rounded-lg border p-3" data-testid={`part-card-${p.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-semibold">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{CATEGORY_LABEL[p.category] || p.category}{p.part_number ? ` · ${p.part_number}` : ""} · {num(p.quantity)} {p.unit || ""}</div>
                        </div>
                        <Button size="iconSm" variant="ghost" onClick={() => openEditPart(p)} aria-label="Rediger"><Pencil className="h-4 w-4" /></Button>
                        <Button size="iconSm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => deletePart(p)} aria-label="Slett"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                  <div className="hidden overflow-x-auto sm:block">
                    <Table data-testid="parts-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Del</TableHead>
                          <TableHead>Kategori</TableHead>
                          <TableHead>Delenr</TableHead>
                          <TableHead className="text-right">Antal pr service</TableHead>
                          <TableHead className="w-24"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parts.map((p) => (
                          <TableRow key={p.id} data-testid={`part-row-${p.id}`}>
                            <TableCell className="font-medium">{p.name}{p.notes ? <div className="text-xs font-normal text-muted-foreground">{p.notes}</div> : null}</TableCell>
                            <TableCell className="text-muted-foreground">{CATEGORY_LABEL[p.category] || p.category}</TableCell>
                            <TableCell className="text-muted-foreground">{p.part_number || "—"}</TableCell>
                            <TableCell className="text-right tabular-nums">{num(p.quantity)} {p.unit || ""}</TableCell>
                            <TableCell>
                              <div className="flex justify-end gap-1">
                                <Button size="iconSm" variant="ghost" onClick={() => openEditPart(p)} data-testid={`edit-part-${p.id}`} aria-label="Rediger"><Pencil className="h-4 w-4" /></Button>
                                <Button size="iconSm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => deletePart(p)} data-testid={`delete-part-${p.id}`} aria-label="Slett"><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Dokument & årskontroll ---- */}
        <TabsContent value="dokument" className="mt-4 space-y-4">
          <MachineDocuments machineId={id} documents={documents} onChange={(docs) => setM((prev) => ({ ...prev, documents: docs }))} />

          <Card className="shadow-sm" data-testid="machine-control-history">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div>
                <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><ShieldCheck className="h-4 w-4 text-primary" /> Årskontrollar</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">Utførte kontrollar – uavhengig av service.</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setControlOpen(true)} data-testid="control-history-register"><ShieldCheck /> Registrer</Button>
            </CardHeader>
            <CardContent className="pt-0">
              {controlHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen årskontrollar registrert enno{m.control_last_date ? ` (sist kontroll frå maskinkortet: ${formatDate(m.control_last_date)})` : ""}.</p>
              ) : (
                <div className="divide-y divide-border rounded-lg border">
                  {controlHistory.map((l) => (
                    <div key={l.id} className="flex flex-col gap-1 px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid={`control-log-${l.id}`}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold tabular-nums">{formatDate(l.performed_date || l.created_at)}</span>
                          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", l.result === "ikkje_godkjent" ? "bg-[rgb(var(--status-overdue))]/15 text-[rgb(var(--status-overdue))]" : l.result === "godkjent_med_merknad" ? "bg-primary/15 text-primary" : "bg-[#1F8A4C]/15 text-[#1F8A4C]")}>{CONTROL_RESULT[l.result] || "Registrert"}</span>
                          {(l.document_ids || []).length > 0 && <span className="text-xs text-muted-foreground">· {l.document_ids.length} vedlegg</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">Neste frist {l.new_date ? formatDate(l.new_date) : "—"}{l.reason && l.reason.includes(" · ") ? ` · ${l.reason.split(" · ").slice(1).join(" · ")}` : ""}</div>
                      </div>
                      <div className="shrink-0 text-xs text-muted-foreground">{l.by_name}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Rapportar ---- */}
        <TabsContent value="rapportar" className="mt-4">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="font-heading text-base font-bold">Rapportar for denne maskina</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {reports.length === 0 ? (
                <EmptyState icon={FileText} title="Ingen rapportar enno" description="Lag ein service- eller jobbrapport for denne maskina."
                  action={<div className="flex gap-2"><Button onClick={() => navigate(`/rapportar/ny-service?machine=${id}`)}><Plus /> Servicerapport</Button><Button variant="outline" onClick={() => navigate(`/rapportar/ny-jobb?machine=${id}`)}><Plus /> Jobbrapport</Button></div>} />
              ) : (
                <div className="-mx-2 divide-y divide-border" data-testid="machine-report-list">
                  {reports.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 px-2 py-3" data-testid={`machine-report-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold">{r.report_number}</span>
                          <ReportTypeBadge type={r.report_type} />
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {formatDate(r.date)} · {r.title || r.service_type || "—"} · {r.technician || "—"}{r.machine_hours != null ? ` · ${num(r.machine_hours)} t` : ""}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setPreviewReport(r)} data-testid={`preview-report-${r.id}`}><Eye /> <span className="hidden sm:inline">Førehandsvis</span></Button>
                      <Button size="sm" variant="ghost" onClick={() => openPdf(r)} data-testid={`open-report-${r.id}`} aria-label="Opne PDF i ny fane"><ExternalLink /></Button>
                      <Button size="sm" variant="ghost" onClick={() => dl(r)} data-testid={`download-report-${r.id}`} aria-label="Last ned"><Download /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Timetal ---- */}
        <TabsContent value="historikk" className="mt-4">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="font-heading text-base font-bold">Timeavlesingar</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setHoursOpen(true)} data-testid="new-reading-button"><Gauge /> Ny avlesing</Button>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="mb-3 text-xs text-muted-foreground">Timetal er berre til informasjon – fristar blir styrte av datoar.</p>
              {(m.hours_readings || []).length > 0 ? (
                <div className="divide-y divide-border rounded-lg border">
                  {[...m.hours_readings].reverse().slice(0, 12).map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2.5 text-sm">
                      <span className="font-semibold tabular-nums">{num(r.hours)} t</span>
                      <span className="text-muted-foreground">{formatDate(r.date)}{r.by ? ` · ${r.by}` : ""}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Ingen avlesingar registrert enno.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <MachineDialog open={editOpen} onOpenChange={setEditOpen} machine={m} onSaved={load} />
      <RegisterControlDialog
        open={controlOpen}
        onOpenChange={setControlOpen}
        machine={m}
        onDone={(updated) => {
          setM((prev) => ({ ...prev, ...updated, parts: prev.parts, reports: prev.reports, deadline_logs: updated.log ? [updated.log, ...(prev.deadline_logs || [])] : prev.deadline_logs }));
          setTab("dokument");
          load(true);
        }}
      />
      <PdfPreviewDialog open={!!previewReport} onOpenChange={(o) => !o && setPreviewReport(null)} report={previewReport} />
      <DeadlineActionDialog
        open={deadlineDialog.open}
        onOpenChange={(o) => setDeadlineDialog((d) => ({ ...d, open: o }))}
        event={deadlineEvent(deadlineDialog.kind)}
        mode={deadlineDialog.mode}
        today={todayISO()}
        onDone={load}
      />

      {/* Reading dialog */}
      <Dialog open={hoursOpen} onOpenChange={setHoursOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="hours-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ny timeavlesing</DialogTitle>
            <DialogDescription>Registrer timetalet du las av på maskina.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Timetal (t)</Label>
              <Input className="h-12 text-lg tabular-nums" type="number" inputMode="decimal" data-testid="hours-input" value={hoursVal} onChange={(e) => setHoursVal(e.target.value)} placeholder={String(m.current_hours ?? "")} autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Avlesingsdato</Label>
              <DatePicker value={hoursDate} onChange={setHoursDate} clearable={false} testid="hours-date-input" />
            </div>
          </DialogBody>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setHoursOpen(false)} data-testid="hours-cancel-button">Avbryt</Button>
            <Button onClick={saveHours} disabled={busy || hoursVal === ""} data-testid="save-hours-button">{busy && <Loader2 className="animate-spin" />} Lagre avlesing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Part dialog */}
      <Dialog open={partOpen} onOpenChange={setPartOpen}>
        <DialogContent className="sm:max-w-md" data-testid="part-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingPart ? "Rediger del" : "Ny del"}</DialogTitle>
            <DialogDescription>Filter, olje, kjølevæske eller anna som trengst ved service.</DialogDescription>
          </DialogHeader>
          <form onSubmit={savePart} className="contents" data-testid="part-form">
            <DialogBody>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Delnamn *</Label>
                <Input className="h-11" data-testid="part-name-input" value={partForm.name} onChange={(e) => setPartForm((f) => ({ ...f, name: e.target.value }))} placeholder="Motoroljefilter" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Kategori</Label>
                  <Select value={partForm.category} onValueChange={(v) => setPartForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger className="h-11" data-testid="part-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`part-category-${o.value}`}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Delenr</Label>
                  <Input className="h-11" data-testid="part-number-input" value={partForm.part_number} onChange={(e) => setPartForm((f) => ({ ...f, part_number: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Antal</Label>
                  <Input className="h-11" type="number" inputMode="decimal" data-testid="part-quantity-input" value={partForm.quantity} onChange={(e) => setPartForm((f) => ({ ...f, quantity: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Eining</Label>
                  <Input className="h-11" value={partForm.unit} onChange={(e) => setPartForm((f) => ({ ...f, unit: e.target.value }))} placeholder="stk, liter …" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Notat</Label>
                <Input className="h-11" value={partForm.notes} onChange={(e) => setPartForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
            </DialogBody>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="ghost" onClick={() => setPartOpen(false)} data-testid="part-cancel-button">Avbryt</Button>
              <Button type="submit" disabled={busy} data-testid="save-part-button">{busy && <Loader2 className="animate-spin" />} Lagre</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={delMachineOpen} onOpenChange={setDelMachineOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette maskin?</AlertDialogTitle>
            <AlertDialogDescription>Dette slettar maskina, alle delar og rapportar permanent. Handlinga kan ikkje angrast.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={deleteMachine} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-machine">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
